export interface Product {
  id: string;
  item_id: string;
  item_name: string;
  price: string;
  sales: string | null;
  shop_name: string | null;
  commission_rate: string | null;
  commission: string | null;
  product_link: string | null;
  offer_link: string | null;
  image_url: string | null;
  description: string | null;
  category: string | null;
  subcategory: string | null;
  featured: boolean;
  banner: boolean;
  national: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProductImage {
  id: string;
  product_id: string;
  image_url: string;
  sort_order: number;
  created_at: string;
}

export interface AuthSession {
  id: string;
  email: string;
}

export interface ClickStat {
  id: string;
  product_id: string;
  link_type: string;
  created_at: string;
  products: {
    item_name: string | null;
    shop_name: string | null;
  } | null;
}

export interface ImportFallbackDiagnostics {
  shell_exec_available: boolean;
  script_path: string | null;
  script_exists: boolean;
  script_candidates: string[];
  node_binary: string | null;
  node_version: {
    ok: boolean;
    output: string | null;
  } | null;
  playwright_check: {
    ok: boolean;
    output: string | null;
  } | null;
  playwright_chromium_executable_path: string | null;
  playwright_chromium_executable_exists: boolean | null;
}

export interface MercadoLivreImportLog {
  exists: boolean;
  size: number;
  lines: string[];
}

export interface MercadoLivreImportEvent {
  id: number;
  created_at: string;
  tag: string;
  status: "success" | "warning" | "error" | "info" | string;
  stage: string | null;
  item_id: string | null;
  source_url: string | null;
  product_link: string | null;
  reason: string | null;
  message: string | null;
  price: string | null;
  description_length: number;
  has_sales: number | boolean;
  payload_json: string | null;
}

export interface MercadoLivreOAuthStatus {
  configured: boolean;
  connected: boolean;
  user_id: number | string | null;
  expires_at: string | null;
  token_path_exists: boolean;
}

function apiUrl(path: string) {
  return new URL(`/api/${path.replace(/^\/+/, "")}`, window.location.origin).toString();
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(apiUrl(path), {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers || {}),
    },
    ...init,
  });

  const text = await response.text();

  try {
    const data = JSON.parse(text);

    if (!response.ok) {
      const message = data?.error || `Erro HTTP ${response.status}`;
      console.error("Erro API:", message, data);
      throw new Error(message);
    }

    return data as T;
  } catch (err) {
    console.error("RESPOSTA INVÁLIDA DO SERVIDOR ↓↓↓");
    console.error(text);
    throw new Error("Resposta inválida do servidor. Veja o console.");
  }
}

export async function getSession() {
  const data = await request<{ user: AuthSession | null }>("auth/me");
  return data.user;
}

export async function login(email: string, password: string) {
  const data = await request<{ user: AuthSession }>("auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
  return data.user;
}

export async function register(email: string, password: string) {
  const data = await request<{ user: AuthSession }>("auth/register", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
  return data.user;
}

export async function logout() {
  await request<{ ok: boolean }>("auth/logout", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function fetchProducts(shopFilter?: string) {
  const query = shopFilter && shopFilter !== "all"
    ? `products?shop=${encodeURIComponent(shopFilter)}`
    : "products";

  return request<Product[]>(query);
}

export async function fetchProduct(id: string) {
  return request<Product>(`products/${id}`);
}

export async function fetchProductImages(productId: string) {
  return request<ProductImage[]>(`products/${productId}/images`);
}

export async function addProductImage(productId: string, imageUrl: string, sortOrder = 0) {
  await request<{ ok: boolean }>(`products/${productId}/images`, {
    method: "POST",
    body: JSON.stringify({
      image_url: imageUrl,
      sort_order: sortOrder,
    }),
  });
}

export async function deleteProductImage(id: string) {
  await request<{ ok: boolean }>(`product-images/${id}`, {
    method: "DELETE",
  });
}

export async function fetchShops(): Promise<string[]> {
  const data = await fetchProducts();
  return [...new Set(data.map((product) => product.shop_name).filter(Boolean) as string[])].sort();
}

export async function deleteProduct(id: string) {
  await request<{ ok: boolean }>(`products/${id}`, {
    method: "DELETE",
  });
}

export async function updateProduct(id: string, data: Partial<Product>) {
  await request<{ ok: boolean }>(`products/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function refreshProductImage(id: string) {
  return request<{ ok: boolean; image_url?: string }>(`products/${id}/refresh-image`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function trackClick(productId: string, linkType = "offer") {
  const data = await request<{ redirect_url?: string | null }>("track-click", {
    method: "POST",
    body: JSON.stringify({
      product_id: productId,
      link_type: linkType,
    }),
  });

  return data.redirect_url || undefined;
}

export function buildTrackedClickUrl(productId: string, linkType = "offer") {
  const query = new URLSearchParams({ type: linkType });
  return apiUrl(`go/${encodeURIComponent(productId)}?${query.toString()}`);
}

export async function queueImportProducts(products: Record<string, string>[]) {
  return request<{ queued: number; total: number }>("import-products-queue", {
    method: "POST",
    body: JSON.stringify({ products }),
  });
}

export async function queueImportProductLink(link: string) {
  return queueImportProducts([{
    product_link: link,
    offer_link: link,
  }]);
}

export async function processImportQueue(batchSize = 5) {
  return request<{
    inserted: number;
    updated: number;
    errors: number;
    processed: number;
    remaining: number;
    total: number;
    error_messages: Array<string | { item_id?: string | null; message?: string | null }>;
  }>("process-import-queue", {
    method: "POST",
    body: JSON.stringify({ batch_size: batchSize }),
  });
}

export async function importProducts(products: Record<string, string>[]) {
  await queueImportProducts(products);
  return processImportQueue();
}

export async function fetchCategories(): Promise<string[]> {
  return request<string[]>("categories");
}

export async function fetchClickStats() {
  return request<ClickStat[]>("clicks");
}

export async function fetchImportFallbackDiagnostics() {
  return request<ImportFallbackDiagnostics>("diagnostics/import-fallback");
}

export async function fetchMercadoLivreImportLog() {
  return request<MercadoLivreImportLog>("diagnostics/mercado-livre-import-log-admin");
}

export async function fetchMercadoLivreImportEvents() {
  return request<MercadoLivreImportEvent[]>("diagnostics/mercado-livre-import-events?limit=200");
}

export async function fetchMercadoLivreOAuthStatus() {
  return request<MercadoLivreOAuthStatus>("mercado-livre/oauth/status");
}

export function mercadoLivreOAuthStartUrl() {
  return apiUrl("mercado-livre/oauth/start");
}
